import { useEffect, useState } from 'react'
import { NavLink, Outlet, useLocation, useNavigate } from 'react-router-dom'
import { LayoutDashboard, Users, PawPrint, Calendar, Receipt, Pill, Package, Syringe, ClipboardList, User, Building2, Hospital, Menu, X, Sun, Moon, UserCheck, ChevronDown, Tag } from 'lucide-react'
import { getMyOrgPractices, getMyPractices } from '../api/staff'
import { useAuth } from '../context/AuthContext'
import { useTheme } from '../context/ThemeContext'
import { useFetch } from '../hooks/useFetch'
import { useIsMobile } from '../hooks/useIsMobile'
import { cn } from '@/lib/utils'
import type { UserRole } from '../types/auth'

const NAV_LINKS = [
  { to: '/dashboard', label: 'Dashboard', icon: LayoutDashboard },
  { to: '/clients', label: 'Clients', icon: Users },
  { to: '/patients', label: 'Patients', icon: PawPrint },
  { to: '/appointments', label: 'Appointments', icon: Calendar },
  { to: '/invoices', label: 'Invoices', icon: Receipt },
  { to: '/stock/products', label: 'Products', icon: Pill },
  { to: '/stock', label: 'Inventory', icon: Package, end: true },
  // Dispensing is a clinical action (backend requires an actual veterinarian) — hidden
  // for org_admin, whose role is org/practice management, not hands-on clinical work.
  { to: '/stock/dispense', label: 'Dispense', icon: Syringe, hideForRoles: ['org_admin'] as UserRole[] },
  { to: '/stock/register', label: 'Drug Register', icon: ClipboardList },
]

const ADMIN_LINKS = [
  { to: '/admin/users', label: 'Users', icon: User },
  { to: '/admin/organizations', label: 'Organisations', icon: Building2 },
  { to: '/admin/practices', label: 'Practices', icon: Hospital },
  { to: '/admin/staff-applications', label: 'Applications', icon: UserCheck },
]

export default function AppLayout() {
  const { user, logout } = useAuth()
  const { theme, toggleTheme } = useTheme()
  const navigate = useNavigate()
  const location = useLocation()
  const isMobile = useIsMobile()
  const [sidebarOpen, setSidebarOpen] = useState(false)
  const [practiceSwitcherOpen, setPracticeSwitcherOpen] = useState(false)

  const isOrgAdmin = user?.role === 'org_admin'

  // Persist selected practice for org_admin across navigation
  const [selectedPracticeId, setSelectedPracticeId] = useState<number | null>(() => {
    const stored = localStorage.getItem('selectedPracticeId')
    return stored ? Number(stored) : null
  })

  useEffect(() => {
    setSidebarOpen(false)
  }, [location.pathname])

  useEffect(() => {
    if (isMobile && sidebarOpen) {
      document.body.style.overflow = 'hidden'
    } else {
      document.body.style.overflow = ''
    }
    return () => { document.body.style.overflow = '' }
  }, [isMobile, sidebarOpen])

  const { data: myPractices } = useFetch(() => getMyPractices(), [])
  const { data: orgPractices } = useFetch(
    () => isOrgAdmin ? getMyOrgPractices() : Promise.resolve([]),
    [isOrgAdmin],
  )

  // Auto-select first org practice if none selected
  useEffect(() => {
    if (isOrgAdmin && orgPractices && orgPractices.length > 0 && !selectedPracticeId) {
      const firstId = orgPractices[0].id
      setSelectedPracticeId(firstId)
      localStorage.setItem('selectedPracticeId', String(firstId))
    }
  }, [isOrgAdmin, orgPractices, selectedPracticeId])

  const selectedPractice = orgPractices?.find(p => p.id === selectedPracticeId)

  const adminPractices = (myPractices ?? []).filter(p => p.is_practice_admin && p.status === 'active')
  const memberPractices = (myPractices ?? []).filter(p => !p.is_practice_admin && p.status === 'active')

  const handleLogout = async () => {
    await logout()
    void navigate('/login', { replace: true })
  }

  const handleSelectPractice = (id: number) => {
    setSelectedPracticeId(id)
    localStorage.setItem('selectedPracticeId', String(id))
    setPracticeSwitcherOpen(false)
  }

  const navLinkClass = ({ isActive }: { isActive: boolean }) =>
    cn(
      'flex items-center gap-[0.6rem] mx-1 px-3 py-[0.55rem] rounded-md no-underline text-sm border-l-2 transition-colors duration-200',
      isActive
        ? 'font-semibold text-white bg-white/10 border-accent'
        : 'font-normal text-white/60 border-transparent hover:text-white/85 hover:bg-white/[0.06]',
    )

  const sidebar = (
    <aside
      className={cn(
        'w-[220px] min-w-[220px] bg-sidebar-bg flex flex-col',
        'md:sticky md:top-0 md:h-screen md:overflow-hidden md:flex',
        'max-md:fixed max-md:inset-y-0 max-md:left-0 max-md:z-[300] max-md:overflow-y-auto max-md:transition-transform max-md:duration-[250ms]',
        sidebarOpen ? 'max-md:translate-x-0' : 'max-md:-translate-x-full',
      )}
    >
      {/* Logo / header */}
      <div className="px-5 pt-6 pb-4 border-b border-white/10 mb-2 flex items-center justify-between">
        <div>
          <div className="font-extrabold text-lg text-white tracking-[-0.02em]">Vet VPMS</div>
          <div className="text-[0.7rem] font-normal text-white/50 mt-[0.15rem] tracking-[0.05em] uppercase">
            Practice Management
          </div>
        </div>
        {isMobile && (
          <button
            onClick={() => setSidebarOpen(false)}
            className="bg-transparent border-0 text-white/70 cursor-pointer p-1 leading-none"
            aria-label="Close menu"
          >
            <X size={20} strokeWidth={1.75} />
          </button>
        )}
      </div>

      {/* Org admin: practice switcher */}
      {isOrgAdmin && orgPractices && orgPractices.length > 0 && (
        <div className="mx-3 mb-2 relative">
          <button
            onClick={() => setPracticeSwitcherOpen(o => !o)}
            className="w-full px-[0.65rem] py-2 rounded bg-white/[0.07] border-l-2 border-white/20 text-left flex items-center justify-between gap-1 hover:bg-white/[0.12] transition-colors"
          >
            <div className="min-w-0">
              {selectedPractice?.organisation_name && (
                <div className="text-[0.7rem] text-white/45 font-medium truncate">
                  {selectedPractice.organisation_name}
                </div>
              )}
              <div className="text-[0.8rem] text-white/85 font-semibold truncate">
                {selectedPractice?.name ?? 'Select practice'}
              </div>
              <div className="text-[0.68rem] text-white/40 mt-[0.1rem]">Org Admin</div>
            </div>
            <ChevronDown
              size={14}
              strokeWidth={1.75}
              className={cn('text-white/40 flex-shrink-0 transition-transform', practiceSwitcherOpen && 'rotate-180')}
            />
          </button>
          {practiceSwitcherOpen && (
            <div className="absolute left-0 right-0 top-full mt-1 bg-sidebar-bg border border-white/15 rounded shadow-lg z-50 overflow-hidden">
              {orgPractices.map(p => (
                <button
                  key={p.id}
                  onClick={() => handleSelectPractice(p.id)}
                  className={cn(
                    'w-full px-3 py-2 text-left text-[0.8rem] hover:bg-white/10 transition-colors',
                    p.id === selectedPracticeId ? 'text-white font-semibold bg-white/[0.08]' : 'text-white/70',
                  )}
                >
                  <div className="truncate">{p.name}</div>
                  {p.organisation_name && (
                    <div className="text-[0.7rem] text-white/40 truncate">{p.organisation_name}</div>
                  )}
                </button>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Staff: org / practice context indicator */}
      {!isOrgAdmin && (myPractices ?? []).length > 0 && (
        <div className="mx-3 mb-2 px-[0.65rem] py-2 rounded bg-white/[0.07] border-l-2 border-white/20">
          {(myPractices ?? []).map((p, i) => (
            <div key={p.id} className={cn((myPractices ?? []).length > 1 && i < (myPractices ?? []).length - 1 && 'mb-2')}>
              {p.organisation_name && (
                <div className="text-[0.7rem] text-white/45 font-medium mb-[0.1rem] truncate">
                  {p.organisation_name}
                </div>
              )}
              <div className="text-[0.8rem] text-white/85 font-semibold truncate">
                {p.name}
              </div>
              <div className="text-[0.68rem] text-white/40 capitalize mt-[0.1rem]">
                {p.is_practice_admin ? 'Admin · ' : ''}{p.clinical_role.replace(/_/g, ' ')}
              </div>
            </div>
          ))}
        </div>
      )}

      <nav className="flex-1 py-2 flex flex-col gap-[2px] overflow-y-auto">
        {(user?.role === 'client'
          ? NAV_LINKS.filter(l => l.to === '/dashboard')
          : NAV_LINKS.filter(l => !user || !('hideForRoles' in l && l.hideForRoles?.includes(user.role)))
        ).map((link) => (
          <NavLink key={link.to} to={link.to} end={'end' in link ? link.end : false} className={navLinkClass}>
            <link.icon size={16} strokeWidth={1.75} />
            {link.label}
          </NavLink>
        ))}

        {adminPractices.length > 0 && (
          <>
            <div className="mx-3 mt-3 mb-1 text-[0.68rem] font-bold text-white/35 uppercase tracking-[0.08em]">
              My Practice{adminPractices.length > 1 ? 's' : ''}
            </div>
            {adminPractices.map(p => (
              <NavLink key={`${p.id}-staff`} to={`/practices/${p.id}/staff`} className={navLinkClass}>
                <Hospital size={16} strokeWidth={1.75} />
                {p.name} — Staff
              </NavLink>
            ))}
            {adminPractices.map(p => (
              <NavLink key={`${p.id}-services`} to={`/practices/${p.id}/services`} className={navLinkClass}>
                <Tag size={16} strokeWidth={1.75} />
                {p.name} — Services
              </NavLink>
            ))}
          </>
        )}

        {memberPractices.length > 0 && adminPractices.length === 0 && !isOrgAdmin && (
          <>
            <div className="mx-3 mt-3 mb-1 text-[0.68rem] font-bold text-white/35 uppercase tracking-[0.08em]">
              My Practice{memberPractices.length > 1 ? 's' : ''}
            </div>
            {memberPractices.map(p => (
              <div key={p.id} className="flex flex-col gap-[2px] px-3 py-[0.4rem] rounded bg-white/[0.05] mb-[2px]">
                <span className="text-[0.8rem] font-semibold text-white/70 flex items-center gap-1">
                  <Hospital size={14} strokeWidth={1.75} /> {p.name}
                </span>
                <span className="text-[0.72rem] text-white/40 capitalize">
                  {p.clinical_role.replace(/_/g, ' ')}
                </span>
              </div>
            ))}
          </>
        )}

        {/* Org admin: link to manage practices in their org */}
        {isOrgAdmin && selectedPracticeId && (
          <>
            <div className="mx-3 mt-3 mb-1 text-[0.68rem] font-bold text-white/35 uppercase tracking-[0.08em]">
              Manage
            </div>
            <NavLink to={`/practices/${selectedPracticeId}/staff`} className={navLinkClass}>
              <Hospital size={16} strokeWidth={1.75} />
              Practice Staff
            </NavLink>
          </>
        )}

        {user?.role === 'super_admin' && (
          <>
            <div className="mx-3 mt-3 mb-1 text-[0.68rem] font-bold text-white/35 uppercase tracking-[0.08em]">
              Admin
            </div>
            {ADMIN_LINKS.map((link) => (
              <NavLink key={link.to} to={link.to} className={navLinkClass}>
                <link.icon size={16} strokeWidth={1.75} />
                {link.label}
              </NavLink>
            ))}
          </>
        )}
      </nav>

      {/* Sidebar footer */}
      <div className="px-3 py-4 border-t border-white/10 mt-auto">
        <div
          className="text-white/70 text-[0.8rem] mb-2 truncate"
          title={user?.email}
        >
          {user?.full_name ?? user?.email}
        </div>
        <NavLink
          to="/profile"
          className={({ isActive }) =>
            cn(
              'block px-3 py-[0.4rem] rounded no-underline text-[0.8rem] mb-[0.4rem] transition-colors',
              isActive
                ? 'text-white bg-white/[0.12]'
                : 'text-white/60 hover:text-white/80 hover:bg-white/[0.08]',
            )
          }
        >
          My Profile
        </NavLink>
        <button
          onClick={toggleTheme}
          className="w-full bg-white/[0.06] border border-white/[0.12] text-white/75 rounded px-3 py-[0.45rem] text-[0.8rem] font-medium cursor-pointer text-left flex items-center gap-2 mb-[0.4rem] hover:bg-white/10 transition-colors"
        >
          {theme === 'dark' ? <Sun size={14} strokeWidth={1.75} /> : <Moon size={14} strokeWidth={1.75} />}
          {theme === 'dark' ? 'Light mode' : 'Dark mode'}
        </button>
        <button
          onClick={() => void handleLogout()}
          className="w-full bg-white/[0.08] border border-white/[0.15] text-white/85 rounded px-3 py-[0.45rem] text-[0.8rem] font-medium cursor-pointer text-left hover:bg-white/[0.12] transition-colors"
        >
          Sign out
        </button>
      </div>
    </aside>
  )

  return (
    <div className="flex min-h-screen bg-bg text-text font-sans">
      {/* Mobile top bar */}
      <header className="md:hidden fixed top-0 left-0 right-0 h-[52px] bg-sidebar-bg flex items-center px-4 z-[200] shadow-[0_1px_4px_rgba(0,0,0,0.2)]">
        <button
          onClick={() => setSidebarOpen(true)}
          className="bg-transparent border-0 text-white cursor-pointer py-1 pr-2 leading-none mr-2"
          aria-label="Open menu"
        >
          <Menu size={22} strokeWidth={1.75} />
        </button>
        <span className="font-bold text-base text-white tracking-[-0.01em]">Vet VPMS</span>
      </header>

      {/* Backdrop overlay (mobile) */}
      {isMobile && sidebarOpen && (
        <div
          onClick={() => setSidebarOpen(false)}
          className="fixed inset-0 bg-black/45 z-[200]"
        />
      )}

      {sidebar}

      <main className="flex-1 bg-bg flex flex-col overflow-auto min-w-0">
        <div className="flex-1 p-4 md:p-8 max-w-[1200px] w-full md:mx-auto box-border mt-[52px] md:mt-0">
          <Outlet />
        </div>
      </main>
    </div>
  )
}
